import { Injectable, Injector, ComponentRef, TemplateRef } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';

import { SheetRef } from './sheet-ref';
import { SHEET_CONTENT_DATA } from './sheet.tokens';
import { SheetContainer } from './sheet-container.component';

export interface SheetContent {
  title?: string | TemplateRef<any>;
  body?: string | TemplateRef<any>;
  closeButton?: boolean;
  actions?: {
    label: string;
    type: 'primary' | 'secondary';
    tag?: string;
  }[];
}

export interface SheetConfig {
  closeOnBackdropClick?: boolean;
  panelClass?: string;
  hasBackdrop?: boolean;
  backdropClass?: string;
  content?: SheetContent;
}

const DEFAULT_CONFIG: SheetConfig = {
  closeOnBackdropClick: true,
  hasBackdrop: true,
  backdropClass: 'cdk-overlay-transparent-backdrop',
  panelClass: 'modal--default',
  content: null
};

@Injectable()
export class SheetService {

  constructor(
    private injector: Injector,
    private overlay: Overlay
  ) { }

  open(config: SheetConfig = {}) {
    // Override default configuration
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };

    // Returns an OverlayRef which is a PortalHost
    const overlayRef = this.createOverlay(dialogConfig);

    // Instantiate remote control
    const modalRef = new SheetRef(overlayRef);

    const overlayComponent = this.attachSheetContainer(overlayRef, dialogConfig, modalRef);

    modalRef.componentInstance = overlayComponent;

    if (dialogConfig.closeOnBackdropClick) {
      overlayRef.backdropClick().subscribe(_ => modalRef.close('cancel'));
    }

    return modalRef;
  }

  private createOverlay(config: SheetConfig) {
    const overlayConfig = this.getOverlayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  private attachSheetContainer(overlayRef: OverlayRef, config: SheetConfig, modalRef: SheetRef) {
    const injector = this.createInjector(config, modalRef);

    const containerPortal = new ComponentPortal(SheetContainer, null, injector);
    const containerRef: ComponentRef<SheetContainer> = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }

  private createInjector(config: SheetConfig, modalRef: SheetRef): PortalInjector {
    const injectionTokens = new WeakMap();

    injectionTokens.set(SheetRef, modalRef);
    injectionTokens.set(SHEET_CONTENT_DATA, config.content);

    return new PortalInjector(this.injector, injectionTokens);
  }

  private getOverlayConfig(config: SheetConfig): OverlayConfig {
    const positionStrategy = this.overlay.position()
      .global();

    const overlayConfig = new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      positionStrategy
    });

    return overlayConfig;
  }
}
